import { PageHero, SectionHeader, NumberedList } from "@/components/PageChrome";
import CTABanner from "@/components/CTABanner";
import { useSEO } from "@/lib/seo";

const TRAITS = [
    {
        title: "Adherence to the virtues.",
        body: "Our attorneys and staff are expected to know and to live the virtues listed on our Values page: Prudence, Justice, Fortitude, Temperance, and the theological virtues of Faith, Hope, and Charity. We are not looking for perfect people. We are looking for people who are working at them, and who want to work in a place that expects them."
    },
    {
        title: "Plain dealing.",
        body: "Written engagement letters, flat fees where they work, no billable-hour theatre. If that sounds like a relief rather than a constraint, you will fit here."
    },
    {
        title: "A disposition to serve.",
        body: "We strive to make one in five estate matters we take on pro bono, though we cannot always hit that target. Everyone on the team touches that work. If service is something you already do without being asked, this is a firm where that instinct is put to use."
    }
];

const PROCESS = [
    {
        title: "A note, not a portal.",
        body: "Send us a short email introducing yourself and telling us why Chesterton in particular. Attach a résumé if you have one; a writing sample if you have one you are proud of. No cover letter template required."
    },
    {
        title: "A conversation.",
        body: "If there is a fit, we schedule a real conversation, ordinarily an hour, about your practice to date, the work you want to do next, and how you think about the parts of it that are not on the résumé."
    },
    {
        title: "A short exercise.",
        body: "For attorney and paralegal roles, we ask you to redline a short document or draft a paragraph on a fact pattern we send over. It is not a trick. It is how we see the craft."
    },
    {
        title: "A decision, promptly.",
        body: "We tell you where we are within a week of the exercise. If it is a yes, we send a written offer with the terms in plain language. If it is a no, we tell you why."
    }
];

export default function Careers() {
    useSEO({
        title: "Careers | Chesterton",
        description:
            "Careers at Chesterton. A small Tennessee firm focused on estate planning, real estate, and business law. Written engagement letters, flat fees where they work, and one pro bono estate plan for every four paid ones.",
        path: "/careers"
    });

    return (
        <>
            <PageHero
                testId="careers-hero"
                kicker="Careers"
                title={<>Wear your convictions on your sleeve.</>}
                intro="We are a small firm today, and we intend to become a larger one, on our own terms. We hire for craft, for character, and for the long haul."
            />

            <section className="py-20 lg:py-28 bg-white border-t border-acre-line" data-testid="careers-who">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
                    <SectionHeader
                        kicker="Who Fits Here"
                        title={<>The kind of lawyer we hire.</>}
                        description="Three dispositions that show up in every hire we make, whether attorney, paralegal, or administrative."
                    />
                    <NumberedList items={TRAITS} />
                </div>
            </section>

            <section className="py-20 lg:py-28 bg-acre-bg border-t border-acre-line" data-testid="careers-openings">
                <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                    <div className="lg:col-span-5">
                        <div className="flex items-center mb-6">
                            <span className="acre-tick" />
                            <span className="text-[11px] uppercase tracking-[0.28em] text-acre-muted">Current Openings</span>
                        </div>
                        <h2 className="font-serif-display text-4xl lg:text-5xl text-acre-secondary leading-tight">
                            Actively hiring lateral attorneys.
                        </h2>
                    </div>
                    <div className="lg:col-span-7 space-y-6 text-acre-muted text-base lg:text-lg leading-relaxed">
                        <p>
                            Chesterton is actively seeking lateral attorneys who can bring a portable book of business and step into meaningful client work from day one. We are open to hires across our core practice areas:
                        </p>
                        <ul className="space-y-2 list-disc pl-6 marker:text-acre-secondary text-acre-ink">
                            <li>Estate planning</li>
                            <li>Elder law</li>
                            <li>Real estate</li>
                            <li>Business transactions</li>
                        </ul>
                        <p>
                            If you have a practice of your own and are looking for a firm where the drafting is careful, the engagements are written, and the culture is grounded in the classical and Christian virtues, we would like to hear from you.
                        </p>
                    </div>
                </div>
            </section>

            <section className="py-20 lg:py-28 bg-white border-t border-acre-line" data-testid="careers-process">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
                    <SectionHeader
                        kicker="How We Hire"
                        title={<>A short, honest process.</>}
                        description="Four steps, ordinarily concluded within a month. We say yes or no in plain language and give reasons either way."
                    />
                    <NumberedList items={PROCESS} />
                </div>
            </section>

            <section className="py-20 lg:py-28 bg-acre-bg border-t border-acre-line" data-testid="careers-apply">
                <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                    <div className="lg:col-span-5">
                        <div className="flex items-center mb-6">
                            <span className="acre-tick" />
                            <span className="text-[11px] uppercase tracking-[0.28em] text-acre-muted">To Apply</span>
                        </div>
                        <h2 className="font-serif-display text-4xl lg:text-5xl text-acre-secondary leading-tight">
                            Write us a note.
                        </h2>
                    </div>
                    <div className="lg:col-span-7 space-y-6 text-acre-muted text-base lg:text-lg leading-relaxed">
                        <p>
                            Email <a href="mailto:info@chesterton.law" className="text-acre-secondary hover:text-acre-primary underline decoration-1 underline-offset-4">info@chesterton.law</a> with a short introduction, a résumé if you have one, and a writing sample if you have one you are proud of. Tell us why Chesterton, in particular. That is the part that matters.
                        </p>
                        <p className="text-acre-ink">
                            We answer every note. If we are not a fit today, we say so, and we tell you why.
                        </p>
                    </div>
                </div>
            </section>

            <CTABanner />
        </>
    );
}
